import {
  AppointmentStatus,
  CalendarEventType,
} from "../../../../types/calendar";
import { GetTransactionsResponse } from "../../../../types/transactions";
import { isOwnTransaction } from "../../../../utils/permissionFilters";

/** The Filters selections (staff / service types / statuses). */
export interface UserFilterSelection {
  selectedArtists: string[];
  selectedServiceTypes: string[];
  selectedStatuses: AppointmentStatus[];
}

const secondaryStaffOf = (event: CalendarEventType): string | undefined =>
  "secondary_staff_id" in event && event.secondary_staff_id
    ? (event.secondary_staff_id as string)
    : undefined;

/** True when the event's primary or secondary staff is in `ids`. */
export function eventStaffIn(
  event: CalendarEventType,
  ids: { has: (id: string) => boolean },
): boolean {
  const secondary = secondaryStaffOf(event);
  return ids.has(event.resourceId) || (!!secondary && ids.has(secondary));
}

/** calendar.scope permission: "own" shows only the user's events. */
export function passesCalendarScope(
  event: CalendarEventType,
  calendarScope: string,
  currentUserId: string,
  transactions: GetTransactionsResponse[],
): boolean {
  if (calendarScope !== "own" || !currentUserId) return true;
  // Mapped time blocks carry their owner only as resourceId (see
  // mapTimeBlocksToEvent); isOwnTimeOff is for raw API records (staff_id).
  if (event.isTimeBlock) return event.resourceId === currentUserId;
  if ("transactionId" in event && event.transactionId) {
    const transaction = transactions.find(
      (t) => t.transaction.id === event.transactionId,
    );
    if (transaction && !isOwnTransaction(transaction as any, currentUserId)) {
      return false;
    }
  }
  return true;
}

/**
 * The Filters dropdown's staff / service type / status match. Time blocks
 * pass the service and status filters. Default statuses hide
 * Cancelled/Declined.
 */
export function passesUserFilters(
  event: CalendarEventType,
  {
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
  }: UserFilterSelection,
): boolean {
  const secondary = secondaryStaffOf(event);
  const matchesArtist =
    selectedArtists.includes("all") ||
    selectedArtists.includes(event.resourceId) ||
    (!!secondary && selectedArtists.includes(secondary));

  const matchesServiceType =
    event.isTimeBlock ||
    selectedServiceTypes.includes("all") ||
    ("serviceType" in event &&
      !!event.serviceType &&
      selectedServiceTypes.includes(event.serviceType as string));

  const matchesStatus =
    event.isTimeBlock ||
    !("status" in event) ||
    selectedStatuses.includes(event.status as AppointmentStatus);

  return matchesArtist && !!matchesServiceType && matchesStatus;
}
